"use client";

import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { encodeEmail, decodeEmail } from "@/lib/obfuscateEmail";

/**
 * For a mailto link whose visible label ISN'T the email itself (a button that says "Get in
 * touch", not the address) -- SanitizedHtml's cloaking covers rich-text content where the
 * address is the visible text, but a plain JSX `<a href="mailto:...">` like this one would still
 * ship the real address as a static href attribute in the page source for a bot to grep. This
 * renders with no href at all until mounted client-side, then fills in the real mailto: link --
 * a real click always still works, since that upgrade happens well before a human could react to
 * the page rendering, but nothing here is a literal, scrapable "mailto:x@y.z" string on load.
 */
export default function ObfuscatedMailtoLink({
  email,
  className,
  children,
}: {
  email: string;
  className?: string;
  children: ReactNode;
}) {
  const [encoded] = useState(() => encodeEmail(email));
  const [href, setHref] = useState<string | undefined>(undefined);

  useEffect(() => {
    setHref(`mailto:${decodeEmail(encoded)}`);
  }, [encoded]);

  return (
    <a href={href} className={className}>
      {children}
    </a>
  );
}
